import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import type { ColDef, ValueGetterParams } from 'ag-grid-community';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { DoorOpen } from 'lucide-react';

import {
	ActiveInactiveCellRenderer,
	DeleteCellRenderer
} from '@/components/grid';
import { ListPageLayout } from '@/components/layout/list-page-layout';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { StatusBadge } from '@/components/ui/status-badge';
import { useResourceNavigation, useGo } from '@/hooks/useResourceNavigation';
import { extractCheckResultError } from '@/lib/checkResult';
import { DEFAULT_PAGE_SIZE } from '@/lib/constants';
import { showMutationSuccessToast } from '@/lib/mutationRefetch';
import { showDeleteColumn } from '@/lib/featureFlags';

import { GateDetails } from './components/gate-details';
import {
	selectGateAfterSave,
	type GateListRecord
} from './gate-list-selection';
import { GATES_LIST_QUERY, GATE_DELETE_MUTATION } from './gates.gql';

interface GatesListResponse {
	terminals?: {
		nodes?: GateListRecord[] | null;
		pageInfo?: {
			hasNextPage: boolean;
			endCursor?: string | null;
		};
	} | null;
}

const getStatusBadge = (gate: GateListRecord) => {
	return <StatusBadge isActive={gate.isActive} />;
};

const GateEntityIcon = () => (
	<div className="flex items-center justify-center w-14 h-14 rounded-full bg-transparent border-2 border-gray-400 dark:border-gray-500 flex-shrink-0">
		<DoorOpen className="w-7 h-7 text-gray-600 dark:text-gray-300" />
	</div>
);

export const GatesList = () => {
	const { t } = useTranslation('gates');
	const apolloClient = useApolloClient();
	const location = useLocation();
	const navigate = useNavigate();
	const [selectedGate, setSelectedGate] = useState<GateListRecord | null>(null);
	const { goToCreate, goToEdit } = useResourceNavigation('gates');
	const go = useGo();
	const { showDialog } = useConfirmationDialog();
	const [itemsToFetch, setItemsToFetch] = useState(DEFAULT_PAGE_SIZE);

	const { data, loading, error, refetch, fetchMore } =
		useQuery<GatesListResponse>(GATES_LIST_QUERY, {
			variables: { first: itemsToFetch },
			fetchPolicy: 'cache-and-network'
		});

	// Pagination state
	const pageInfo = data?.terminals?.pageInfo;
	const hasNextPage = pageInfo?.hasNextPage ?? false;
	const endCursor = pageInfo?.endCursor;
	const [isLoadingMore, setIsLoadingMore] = useState(false);

	const handleLoadMore = useCallback(async () => {
		if (!hasNextPage || isLoadingMore || !endCursor) return;

		setIsLoadingMore(true);
		try {
			await fetchMore({
				variables: {
					first: DEFAULT_PAGE_SIZE,
					after: endCursor
				},
				updateQuery: (prev, { fetchMoreResult }) => {
					if (!fetchMoreResult) return prev;
					const newNodes = fetchMoreResult.terminals?.nodes || [];
					const prevNodes = prev.terminals?.nodes || [];
					setItemsToFetch(prevNodes.length + newNodes.length);
					setLocalGates([]);
					return {
						...prev,
						terminals: {
							...fetchMoreResult.terminals,
							nodes: [...prevNodes, ...newNodes]
						}
					};
				}
			});
		} finally {
			setIsLoadingMore(false);
		}
	}, [hasNextPage, isLoadingMore, endCursor, fetchMore]);

	const handleRefetch = useCallback(() => {
		setItemsToFetch(DEFAULT_PAGE_SIZE);
		setLocalGates([]);
		refetch({ first: DEFAULT_PAGE_SIZE });
	}, [refetch]);

	const [deleteGate, { loading: deleteLoading }] = useMutation(
		GATE_DELETE_MUTATION
	);

	// Local state to manage gate list data with imperative updates
	const [localGates, setLocalGates] = useState<GateListRecord[]>([]);

	const gates = useMemo(() => {
		const serverGates = data?.terminals?.nodes ?? [];
		// Always prefer server data - local state is only for optimistic updates
		if (localGates.length === 0 || serverGates.length === 0) {
			return serverGates;
		}
		// Use local state only when both have data (for optimistic updates)
		return localGates;
	}, [data?.terminals?.nodes, localGates]);

	// Update local gates when query data changes (but only if no local override exists)
	useEffect(() => {
		if (!loading && data?.terminals?.nodes && localGates.length === 0) {
			setLocalGates(data.terminals.nodes);
		}
	}, [data?.terminals?.nodes, loading, localGates.length]);

	// Handle gate selection from location state (after create/edit)
	useEffect(() => {
		const state = location.state as { selectGateId?: string } | null;
		if (state?.selectGateId && gates.length > 0 && !loading) {
			const gateIdToSelect = state.selectGateId;
			// Trigger the selection after a short delay to ensure grid is ready
			const timeoutId = setTimeout(async () => {
				try {
					await selectGateAfterSave({
						gateId: gateIdToSelect,
						client: apolloClient,
						currentGates: gates,
						onDataUpdate: setLocalGates,
						onGateSelect: setSelectedGate
					});
					// Clear the location state after processing to prevent re-triggering
					navigate(location.pathname, { replace: true, state: null });
				} catch (error) {
					console.error(
						'Failed to select gate after navigation:',
						error
					);
				}
			}, 100);

			return () => clearTimeout(timeoutId);
		}
	}, [
		location.state,
		gates.length,
		loading,
		apolloClient,
		gates,
		navigate,
		location.pathname
	]);

	const handleDeleteGate = async (gate: GateListRecord) => {
		const nameDisplay = gate.name?.trim() || 'Unknown gate';
		const confirmed = await showDialog(
			'Delete Gate',
			`Are you sure you want to delete gate "${nameDisplay}"? This action cannot be undone.`
		);

		if (confirmed) {
			const wasSelectedGate = selectedGate?.terminalId === gate.terminalId;

			if (wasSelectedGate) {
				setSelectedGate(null);
			}

			try {
				const result = await deleteGate({
					variables: {
						input: {
							terminalId: gate.terminalId
						}
					},
					refetchQueries: ['Terminals']
				});

				const checkResult = result.data?.deleteTerminal;

				if (checkResult?.success) {
					showMutationSuccessToast('gates', 'delete');
					setLocalGates([]);
					refetch();
				} else {
					const errorMessage = extractCheckResultError(checkResult);
					toast.error(`Failed to delete gate: ${errorMessage}`);
				}
			} catch (error) {
				console.error('Delete gate error:', error);
				toast.error('Failed to delete gate. Please try again.');
			}
		}
	};

	const handleDeleteGateById = async (gateId: string) => {
		const gate = gates.find(g => g.terminalId === gateId);
		if (gate) {
			await handleDeleteGate(gate);
		}
	};

	const handleCloneGate = (gateId: string) => {
		const gate = gates.find(g => g.terminalId === gateId);
		if (gate) {
			go({
				to: '/gates/create',
				state: {
					cloneData: {
						// Add "(Copy)" suffix to name for clarity
						name: gate.name ? `${gate.name} (Copy)` : gate.name,
						ipAddress: gate.ipAddress,
						gateKey: gate.gateKey,
						// Use top-level terminalTypeId or fall back to nested property
						terminalTypeId: gate.terminalTypeId ?? gate.terminalType?.terminalTypeId,
						isActive: gate.isActive
					}
				}
			});
		}
	};

	const columns: ColDef<GateListRecord>[] = useMemo(
		() => [
			{
				headerName: t('list.columns.name'),
				field: 'name',
				minWidth: 160,
				flex: 1,
				valueGetter: (params: ValueGetterParams<GateListRecord>) =>
					params.data?.name ?? 'Unknown',
				cellRenderer: (params: ValueGetterParams<GateListRecord>) => {
					const nameDisplay = params.data?.name ?? 'Unknown';
					const typeLabel = params.data?.terminalType?.name ?? t('details.unassigned');
					return (
						<div className="flex flex-col py-1 overflow-hidden min-w-0 w-full">
							<div className="font-medium truncate">{nameDisplay}</div>
							<div className="text-xs text-muted-foreground truncate">
								{typeLabel}
							</div>
						</div>
					);
				}
			},
			{
				headerName: t('list.columns.ipAddress'),
				field: 'ipAddress',
				minWidth: 140,
				flex: 1,
				valueGetter: (params: ValueGetterParams<GateListRecord>) =>
					params.data?.ipAddress ?? '—'
			},
			{
				headerName: t('list.columns.status'),
				field: 'isActive',
				width: 120,
				minWidth: 120,
				cellRenderer: ActiveInactiveCellRenderer
			},
			...(showDeleteColumn ? [{
				headerName: '',
				field: undefined,
				width: 60,
				minWidth: 60,
				maxWidth: 60,
				cellRenderer: DeleteCellRenderer,
				cellRendererParams: {
					onDelete: handleDeleteGate,
					disabled: deleteLoading
				},
				sortable: false,
				filter: false,
				resizable: false
			}] : [])
		],
		[t, handleDeleteGate, deleteLoading]
	);

	return (
		<>
			{error && (
				<div className="flex items-start p-4">
					<Alert variant="destructive" className="max-w-full">
						<AlertTitle>Unable to load gates</AlertTitle>
						<AlertDescription>
							{error.message ??
								'Failed to retrieve gate data.'}
						</AlertDescription>
					</Alert>
				</div>
			)}
			<ListPageLayout
				gridId="gates-list"
				columns={columns}
				data={gates}
				title={t('list.title')}
				description={t('list.description')}
				searchPlaceholder={t('list.searchPlaceholder')}
				isLoading={loading || deleteLoading}
				selectedItem={selectedGate}
				detailsTitle="Gate Details"
				onRowClick={setSelectedGate}
				onCreate={goToCreate}
				onEdit={gateId => goToEdit(gateId)}
				onDelete={handleDeleteGateById}
				onClone={handleCloneGate}
				onRefresh={handleRefetch}
				getRecordId={gate => gate.terminalId}
				renderDetailsContent={gate => (
					<GateDetails gateId={gate.terminalId} />
				)}
				getDetailsDescription={gate =>
					gate.name ?? 'Unnamed gate'
				}
				getStatusBadge={getStatusBadge}
				renderEntityIcon={() => <GateEntityIcon />}
				getEntityTypeName={gate =>
					gate.isActive ? t('details.activeGate') : t('details.inactiveGate')
				}
				hasNextPage={hasNextPage}
				isLoadingMore={isLoadingMore}
				onLoadMore={handleLoadMore}
				entityName={t('entityName')}
			/>
		</>
	);
};
